import { FC, useCallback, useMemo } from "react";
import { Stack } from "@mui/material";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { normalizedCostWidgetSetupAggregationsSelector } from "@/store/dashboards/selectors/setups/normalized-cost-widget/normalizedCostWidgetSetupAggregationsSelector";
import { WidgetNormalizedCostAggregation } from "@/services/cloudchipr.api";
import { normalizedCostWidgetAggregationChangeThunk } from "@/store/dashboards/thunks/widgets/normalized-cost-widget/setup-change/normalizedCostWidgetAggregationChangeThunk";
import { WidgetConfigSection } from "@/components/pages/dashboard/components/adding-widget/widget-create/widget-setups/common/widget-configurations/WidgetConfigSection";
import AggregationsIcon from "@/assets/icons/app-related/aggregations_icon.svg";
import { NormalizedCostWidgetAggregationSwitch } from "@/components/pages/dashboard/components/adding-widget/widget-create/widget-setups/normalized-cost-widget/side-bar/aggregations/NormalizedCostWidgetAggregationSwitch";

export const NormalizedCostWidgetAggregationsSection: FC = () => {
  const dispatch = useAppDispatch();
  const aggregations = useAppSelector(
    normalizedCostWidgetSetupAggregationsSelector,
  );

  const switchChecked = useMemo(
    () =>
      Object.keys(aggregations ?? {}).some(
        (key) => !!aggregations?.[key as keyof WidgetNormalizedCostAggregation],
      ),
    [aggregations],
  );

  const handleSwitchChange = useCallback(() => {
    const aggregationsNewState = Object.keys(aggregations ?? {})?.reduce(
      (acc, key) => {
        acc[key as keyof WidgetNormalizedCostAggregation] = !switchChecked;
        return acc;
      },
      {} as WidgetNormalizedCostAggregation,
    );

    dispatch(normalizedCostWidgetAggregationChangeThunk(aggregationsNewState));
  }, [aggregations, switchChecked, dispatch]);

  return (
    <WidgetConfigSection
      title="Aggregations"
      icon={<img src={AggregationsIcon} alt="Aggregations Icon" />}
      switchChecked={switchChecked}
      onSwitchChange={handleSwitchChange}
    >
      <Stack spacing={1.5}>
        {normalizedWidgetAggregations.map((key) => (
          <NormalizedCostWidgetAggregationSwitch
            key={key}
            aggregationKey={key}
          />
        ))}
      </Stack>
    </WidgetConfigSection>
  );
};

const normalizedWidgetAggregations: Array<
  keyof WidgetNormalizedCostAggregation
> = [
  "actual_total_cost",
  "average_normalized_monthly_cost",
  "forecasted_month_end_cost",
];
